import React, { Dispatch, SetStateAction, createContext, useContext, useState } from "react";

import {
  CMC_PH_TYPE,
  CMC_SOIL_TYPE,
  CROP_ROTATION,
  CropFCPO,
  CropItemData,
  CropMixComposerGoalResponse,
  GOAL_LEVEL_2,
  GoalScore,
  MainCropSettingsCMC,
  MixDistributionShare,
} from "models";

import { CMC_ONBOARDING_1_ERRORS, CMC_ROUTES } from "pages/app/crop-mix-composer/CropMixComposerOverlay";
import { TimelineCalendarItemsProps } from "pages/app/field-timeline/components/FieldDetailsTimeline";

export type CropMixComposerContextType = {
  currentStep: CMC_ROUTES | null;
  activeCropMixId?: string | undefined;
  soilType?: CMC_SOIL_TYPE | undefined;
  soilPh?: CMC_PH_TYPE | undefined;
  drought?: boolean | undefined;
  mainCropSettings?: MainCropSettingsCMC | undefined;
  selectedGoals: GOAL_LEVEL_2[];
  weightShareDistribution: MixDistributionShare;
  aggregateGoalScores: GoalScore[];
  aggregateMatchScore: number;
  selectedCrops: CropMixComposerGoalResponse[];
  regionId: string | undefined;
  isPageLoading: boolean;
  isChangingStep: "next" | "previous" | null;
  cropRotationFamiliesToExclude: CROP_ROTATION[];
  legumeExclusionConfirmation: boolean | null;
  showDrawerFromCMC: boolean;
  selectedRecommendationItemOnTimeline?: TimelineCalendarItemsProps<CropItemData>;
  cmcMixCropFCPO: CropFCPO;
  isNewCropMix: boolean;
  potentialPayout: number;
  onboarding1Errors: CMC_ONBOARDING_1_ERRORS[];
  autoMixMode: boolean;
  showAutoMixFailureMessage: boolean;
  setActiveCropMixId: Dispatch<SetStateAction<string | undefined>>;
  setCurrentStep: Dispatch<SetStateAction<CMC_ROUTES | null>>;
  setSoilType: Dispatch<SetStateAction<CMC_SOIL_TYPE | undefined>>;
  setSoilPh: Dispatch<SetStateAction<CMC_PH_TYPE | undefined>>;
  setDrought: Dispatch<SetStateAction<boolean | undefined>>;
  setMainCropSettings: Dispatch<SetStateAction<MainCropSettingsCMC | undefined>>;
  setSelectedGoals: Dispatch<SetStateAction<GOAL_LEVEL_2[]>>;
  setWeightShareDistribution: Dispatch<SetStateAction<MixDistributionShare>>;
  setAggregateGoalScores: Dispatch<SetStateAction<GoalScore[]>>;
  setAggregateMatchScore: Dispatch<SetStateAction<number>>;
  setSelectedCrops: Dispatch<SetStateAction<CropMixComposerGoalResponse[]>>;
  setRegionId: Dispatch<SetStateAction<string | undefined>>;
  setIsPageLoading: Dispatch<SetStateAction<boolean>>;
  setIsChangingStep: Dispatch<SetStateAction<"next" | "previous" | null>>;
  setCropRotationFamiliesToExclude: Dispatch<SetStateAction<CROP_ROTATION[]>>;
  setLegumeExclusionConfirmation: Dispatch<SetStateAction<boolean | null>>;
  setShowDrawerFromCMC: Dispatch<SetStateAction<boolean>>;
  setSelectedRecommendationItemOnTimeline: Dispatch<SetStateAction<TimelineCalendarItemsProps<CropItemData> | undefined>>;
  setCMCMixCropFCPO: Dispatch<SetStateAction<CropFCPO>>;
  setIsNewCropMix: Dispatch<SetStateAction<boolean>>;
  setPotentialPayout: Dispatch<SetStateAction<number>>;
  setOnboarding1Errors: Dispatch<SetStateAction<CMC_ONBOARDING_1_ERRORS[]>>;
  setAutoMixMode: Dispatch<SetStateAction<boolean>>;
  setShowAutoMixFailureMessage: Dispatch<SetStateAction<boolean>>;
};

const CropMixComposerContext = createContext<CropMixComposerContextType>({
  currentStep: null,
  activeCropMixId: undefined,
  selectedGoals: [],
  weightShareDistribution: {},
  aggregateGoalScores: [] as GoalScore[],
  aggregateMatchScore: 0,
  selectedCrops: [],
  regionId: undefined,
  isPageLoading: false,
  isChangingStep: null,
  cropRotationFamiliesToExclude: [],
  legumeExclusionConfirmation: null,
  showDrawerFromCMC: false,
  selectedRecommendationItemOnTimeline: undefined,
  cmcMixCropFCPO: {} as CropFCPO,
  isNewCropMix: false,
  potentialPayout: 0,
  onboarding1Errors: [],
  autoMixMode: true,
  showAutoMixFailureMessage: false,
  setCurrentStep: () => null,
  setActiveCropMixId: () => null,
  setSoilType: () => null,
  setSoilPh: () => null,
  setDrought: () => null,
  setMainCropSettings: () => null,
  setSelectedGoals: () => null,
  setWeightShareDistribution: () => null,
  setAggregateGoalScores: () => [],
  setAggregateMatchScore: () => null,
  setSelectedCrops: () => null,
  setRegionId: () => null,
  setIsPageLoading: () => null,
  setIsChangingStep: () => null,
  setCropRotationFamiliesToExclude: () => null,
  setLegumeExclusionConfirmation: () => null,
  setShowDrawerFromCMC: () => null,
  setSelectedRecommendationItemOnTimeline: () => null,
  setCMCMixCropFCPO: () => null,
  setIsNewCropMix: () => null,
  setPotentialPayout: () => null,
  setOnboarding1Errors: () => null,
  setAutoMixMode: () => null,
  setShowAutoMixFailureMessage: () => null,
});

export const useCropMixComposerContext = () => useContext(CropMixComposerContext);

export const CropMixComposerContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentStep, setCurrentStep] = useState<CMC_ROUTES | null>(null);
  const [activeCropMixId, setActiveCropMixId] = useState<string | undefined>();
  const [soilType, setSoilType] = useState<CMC_SOIL_TYPE | undefined>();
  const [soilPh, setSoilPh] = useState<CMC_PH_TYPE | undefined>();
  const [drought, setDrought] = useState<boolean | undefined>();
  const [mainCropSettings, setMainCropSettings] = useState<MainCropSettingsCMC | undefined>();
  const [selectedGoals, setSelectedGoals] = useState<GOAL_LEVEL_2[]>([]);
  const [weightShareDistribution, setWeightShareDistribution] = useState<MixDistributionShare>({});
  const [aggregateGoalScores, setAggregateGoalScores] = useState<GoalScore[]>([]);
  const [aggregateMatchScore, setAggregateMatchScore] = useState<number>(0);
  const [selectedCrops, setSelectedCrops] = useState<CropMixComposerGoalResponse[]>([]);
  const [regionId, setRegionId] = useState<string | undefined>();
  const [isPageLoading, setIsPageLoading] = useState<boolean>(false);
  const [isChangingStep, setIsChangingStep] = useState<"next" | "previous" | null>(null);
  const [cropRotationFamiliesToExclude, setCropRotationFamiliesToExclude] = useState<CROP_ROTATION[]>([]);
  const [legumeExclusionConfirmation, setLegumeExclusionConfirmation] = useState<boolean | null>(null);
  const [showDrawerFromCMC, setShowDrawerFromCMC] = useState<boolean>(false);
  const [selectedRecommendationItemOnTimeline, setSelectedRecommendationItemOnTimeline] = useState<TimelineCalendarItemsProps<CropItemData> | undefined>();
  const [cmcMixCropFCPO, setCMCMixCropFCPO] = useState<CropFCPO>({} as CropFCPO);
  const [isNewCropMix, setIsNewCropMix] = useState<boolean>(false);
  const [potentialPayout, setPotentialPayout] = useState<number>(0);
  const [onboarding1Errors, setOnboarding1Errors] = useState<CMC_ONBOARDING_1_ERRORS[]>([]);
  const [autoMixMode, setAutoMixMode] = useState<boolean>(true);
  const [showAutoMixFailureMessage, setShowAutoMixFailureMessage] = useState<boolean>(false);

  return (
    <CropMixComposerContext.Provider
      value={{
        currentStep,
        activeCropMixId,
        setActiveCropMixId,
        soilType,
        soilPh,
        drought,
        mainCropSettings,
        selectedGoals,
        weightShareDistribution,
        aggregateGoalScores,
        aggregateMatchScore,
        selectedCrops,
        regionId,
        isPageLoading,
        isChangingStep,
        cropRotationFamiliesToExclude,
        legumeExclusionConfirmation,
        showDrawerFromCMC,
        selectedRecommendationItemOnTimeline,
        cmcMixCropFCPO,
        isNewCropMix,
        potentialPayout,
        onboarding1Errors,
        autoMixMode,
        showAutoMixFailureMessage,
        setCurrentStep,
        setSoilType,
        setSoilPh,
        setDrought,
        setMainCropSettings,
        setSelectedGoals,
        setWeightShareDistribution,
        setAggregateGoalScores,
        setAggregateMatchScore,
        setSelectedCrops,
        setRegionId,
        setIsPageLoading,
        setIsChangingStep,
        setCropRotationFamiliesToExclude,
        setLegumeExclusionConfirmation,
        setShowDrawerFromCMC,
        setSelectedRecommendationItemOnTimeline,
        setCMCMixCropFCPO,
        setIsNewCropMix,
        setPotentialPayout,
        setOnboarding1Errors,
        setAutoMixMode,
        setShowAutoMixFailureMessage,
      }}
    >
      {children}
    </CropMixComposerContext.Provider>
  );
};
